import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { Button } from "@progress/kendo-react-buttons";
import { Input, RadioButton } from "@progress/kendo-react-inputs";
import { useDealReward } from "src/dm/hooks/useDealRewards";
import { useDealItems } from "src/dm/hooks/useDealItems";
import {
  useAddRewardFamily,
  useAddRewardItem,
  useAddRewardItemGroup,
  useDeleteRewardFamily,
  useDeleteRewardItem,
  useDeleteRewardItemGroup,
  usePromoteRewardItemsToMandatory,
  useRewardAvailableFamilies,
  useRewardFamilies,
  useRewardItemGroups,
  useRewardItems,
} from "src/dm/hooks/useDealRewardSub";
import {
  useRewardBuyQtyCodes,
  useRewardGetQtyCodes,
} from "src/dm/hooks/useRewardQtyCodes";
import * as s from "./EditRewardItems.css";

/**
 * Reward Items page (rebuild — mirrors legacy `Reward/EditRewardItems.aspx`).
 *
 * The legacy page has THREE stacked sections:
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Card 1 — Reward Header Information                           │
 *   │   read-only: Description / Promotion Code / Buy / For-Get    │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ Card 2 — Reward Item Group Information                       │
 *   │   left: listbox of existing reward groups +                  │
 *   │         "Add Mandatory" / "Add Reward" / "Remove" links      │
 *   │   right: selected-group detail (type, number, amt, pct, free)│
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ Card 3 — Items for Selected Group                            │
 *   │   radio: ( ) Family   ( ) UPC                                │
 *   │   two-listbox  +  >  >>  <  <<  +  Scan Quantity             │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * The previous SPA implementation had a TabStrip ("Add by Group" / "Add by
 * UPC") whose tree showed CATALOG MERCHANDISING GROUPS from
 * RP_ITEM_AUTH_BANNERS_MV.GRP_CD — wrong table entirely. The legacy
 * concept of "reward item group" is a LOGICAL M/R bucket inside the
 * single reward (DEAL_REWARD_PROMO_ITEM_GRP), not an item-master
 * merchandising group. This rewrite restores the original model.
 */

// Legacy formats the listbox row as "<Mandatory|Reward> Group <N>"
// (see Reward/EditRewardItems.aspx.cs:287). Keep parity so user-facing
// strings match.
const itemTypeLabel = (code: string): string => {
  switch (code) {
    case "M":
      return "Mandatory";
    case "R":
      return "Reward";
    case "B":
      return "Buy";
    case "G":
      return "Get";
    default:
      return code;
  }
};

const groupListLabel = (g: { rewardItemTypeCode: string; rewardItemGroupId: number }) =>
  `${itemTypeLabel(g.rewardItemTypeCode)} Group ${g.rewardItemGroupId}`;

const composeKey = (typeCode: string, groupId: number) => `${typeCode}:${groupId}`;

// FormRow: identical helper to EditReward.FormRow — drops a right-aligned
// label + input cell into the canonical .formGrid (180px / 1fr).
const FormRow = ({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Fragment>
    <label className={s.fieldLabel}>{label}</label>
    <div className={s.inputCell}>{children}</div>
  </Fragment>
);

const EditRewardItems = () => {
  const { dealId: dealIdParam } = useParams<{ dealId: string }>();
  const dealId = dealIdParam ? Number(dealIdParam) : 0;

  // ---- Queries ------------------------------------------------------------
  const rewardQ = useDealReward(dealId);
  const groupsQ = useRewardItemGroups(dealId);
  const itemsQ = useRewardItems(dealId);
  const familiesQ = useRewardFamilies(dealId);
  const availFamiliesQ = useRewardAvailableFamilies(dealId);
  const dealItemsQ = useDealItems(dealId);
  const buyCodesQ = useRewardBuyQtyCodes();
  const getCodesQ = useRewardGetQtyCodes();

  // ---- Mutations ----------------------------------------------------------
  const addGroup = useAddRewardItemGroup(dealId);
  const delGroup = useDeleteRewardItemGroup(dealId);
  const addFamily = useAddRewardFamily(dealId);
  const delFamily = useDeleteRewardFamily(dealId);
  const addItem = useAddRewardItem(dealId);
  const delItem = useDeleteRewardItem(dealId);
  const promote = usePromoteRewardItemsToMandatory(dealId);

  // ---- Local state --------------------------------------------------------
  // PK of the selected reward group is composite (itemTypeCode + groupId);
  // hold both so the Card 3 transfer panel can scope its operations.
  const [selectedGroup, setSelectedGroup] = useState<{
    typeCode: string;
    groupId: number;
  } | null>(null);

  // Radio: add-by Family (default, matches legacy rbItemMode) vs UPC.
  const [addMode, setAddMode] = useState<"Family" | "Upc">("Family");

  // Multi-select state for both listboxes in Card 3 (clicks toggle selection).
  const [availableSelected, setAvailableSelected] = useState<string[]>([]);
  const [assignedSelected, setAssignedSelected] = useState<string[]>([]);

  // Scan Quantity textbox value — used at the moment a row is added.
  const [scanQty, setScanQty] = useState<string>("");

  // Generic status pill text below the transfer controls.
  const [status, setStatus] = useState<{
    text: string;
    ok: boolean;
  } | null>(null);

  // Auto-select the first group when the list first loads or when the
  // currently-selected group disappears (e.g. after a delete).
  useEffect(() => {
    const groups = groupsQ.data ?? [];
    if (groups.length === 0) {
      if (selectedGroup) setSelectedGroup(null);
      return;
    }
    const stillThere =
      selectedGroup &&
      groups.some(
        (g) =>
          g.rewardItemTypeCode === selectedGroup.typeCode &&
          g.rewardItemGroupId === selectedGroup.groupId
      );
    if (!stillThere) {
      const first = groups[0];
      setSelectedGroup({
        typeCode: first.rewardItemTypeCode,
        groupId: first.rewardItemGroupId,
      });
    }
  }, [groupsQ.data, selectedGroup]);

  // Clear transfer-listbox selections whenever the selected group changes
  // or the mode flips. Otherwise stale selections from one group/mode
  // leak into the next.
  useEffect(() => {
    setAvailableSelected([]);
    setAssignedSelected([]);
    setStatus(null);
  }, [selectedGroup?.typeCode, selectedGroup?.groupId, addMode]);

  // ---- Card 1 derived values ----------------------------------------------
  // Buy / For-Get labels mirror the legacy aspx.cs (EditRewardItems.aspx.cs
  // lines 202-217): show "Buy : <qty> <code-desc>" and either "For : <amt>"
  // when VendorNetAmount is set OR "Get : <qty> <get-code-desc>" otherwise.
  const reward = rewardQ.data;
  const buyCodeDesc = useMemo(() => {
    if (!reward?.vendorBuyQtyCode || !buyCodesQ.data) return null;
    return (
      buyCodesQ.data.find((c) => c.id === reward.vendorBuyQtyCode)
        ?.description ?? null
    );
  }, [reward?.vendorBuyQtyCode, buyCodesQ.data]);

  const getCodeDesc = useMemo(() => {
    if (!reward?.vendorGetQtyCode || !getCodesQ.data) return null;
    return (
      getCodesQ.data.find((c) => c.id === reward.vendorGetQtyCode)
        ?.description ?? null
    );
  }, [reward?.vendorGetQtyCode, getCodesQ.data]);

  const buyDisplay = useMemo(() => {
    if (reward?.vendorBuyQty == null) return "—";
    return `${reward.vendorBuyQty.toFixed(2)}${buyCodeDesc ? ` ${buyCodeDesc}` : ""}`;
  }, [reward?.vendorBuyQty, buyCodeDesc]);

  // `For` mode when VendorNetAmount is set; otherwise `Get` mode (legacy).
  const forGetMode: "For" | "Get" = reward?.vendorNetAmount != null ? "For" : "Get";
  const forGetDisplay = useMemo(() => {
    if (!reward) return "—";
    if (forGetMode === "For") {
      return reward.vendorNetAmount != null
        ? `$${reward.vendorNetAmount.toFixed(2)}`
        : "—";
    }
    if (reward.vendorGetQty == null) return "—";
    return `${reward.vendorGetQty.toFixed(2)}${getCodeDesc ? ` ${getCodeDesc}` : ""}`;
  }, [reward, forGetMode, getCodeDesc]);

  const promoCodeDisplay = useMemo(() => {
    if (!reward) return "—";
    const code = reward.promotionCode;
    const desc = reward.description;
    if (code == null && !desc) return "—";
    if (code != null && desc) return `${code} ${desc}`;
    if (code != null) return String(code);
    return desc ?? "—";
  }, [reward]);

  // ---- Card 2 derived values ----------------------------------------------
  // Sort groups: Mandatory before Reward (legacy: orderby ItemType, GroupId).
  const sortedGroups = useMemo(() => {
    const groups = groupsQ.data ?? [];
    return [...groups].sort((a, b) => {
      if (a.rewardItemTypeCode !== b.rewardItemTypeCode)
        return a.rewardItemTypeCode.localeCompare(b.rewardItemTypeCode);
      return a.rewardItemGroupId - b.rewardItemGroupId;
    });
  }, [groupsQ.data]);

  const selectedGroupRow = useMemo(() => {
    if (!selectedGroup) return null;
    return (
      sortedGroups.find(
        (g) =>
          g.rewardItemTypeCode === selectedGroup.typeCode &&
          g.rewardItemGroupId === selectedGroup.groupId
      ) ?? null
    );
  }, [sortedGroups, selectedGroup]);

  // ---- Card 3 derived: assigned vs available ------------------------------
  //
  // FAMILY MODE
  //   assigned  = DealRewardFamily rows scoped to (selectedType, selectedGroupId)
  //   available = DealRewardAvailableFamily rows where RowType=Family and
  //               family is not already assigned to this (type, group).
  //
  // UPC MODE
  //   assigned  = DealRewardItem rows scoped to (selectedType, selectedGroupId)
  //               joined to DealItem for retailItemNumber + description.
  //   available = DealItem rows not already in the assigned set.

  const assignedFamilies = useMemo(() => {
    if (!selectedGroup) return [];
    return (familiesQ.data ?? []).filter(
      (f) =>
        f.rewardItemTypeCode === selectedGroup.typeCode &&
        (f.rewardItemGroupId ?? -1) === selectedGroup.groupId
    );
  }, [familiesQ.data, selectedGroup]);

  const availableFamilies = useMemo(() => {
    if (!selectedGroup) return [];
    const assigned = new Set(assignedFamilies.map((f) => String(f.familyCode)));
    return (availFamiliesQ.data ?? [])
      .filter(
        (av) =>
          av.rowType === "Family" &&
          av.rowId != null &&
          // Legacy view returns rows scoped to the reward; available-families
          // for the OPPOSITE itemType (e.g. Reward families when we're
          // looking at a Mandatory group) are still valid candidates because
          // a family can be added to either side. We DO filter out anything
          // already in the assigned set, though.
          !assigned.has(String(av.rowId))
      )
      .sort((a, b) =>
        (a.description ?? "").localeCompare(b.description ?? "")
      );
  }, [availFamiliesQ.data, assignedFamilies, selectedGroup]);

  const dealItemMap = useMemo(() => {
    const m = new Map<
      number,
      { retailItemNumber: number; description: string | null }
    >();
    for (const di of dealItemsQ.data ?? []) {
      m.set(di.dealItemId, {
        retailItemNumber: di.retailItemNumber,
        description: di.itemDescription,
      });
    }
    return m;
  }, [dealItemsQ.data]);

  const assignedItems = useMemo(() => {
    if (!selectedGroup) return [];
    return (itemsQ.data ?? [])
      .filter(
        (it) =>
          it.rewardItemTypeCode === selectedGroup.typeCode &&
          (it.rewardItemGroupId ?? -1) === selectedGroup.groupId &&
          !it.addedByFamily
      )
      .map((it) => {
        const di = it.dealItemId ? dealItemMap.get(it.dealItemId) : undefined;
        return {
          rewardItemId: it.rewardItemId,
          dealItemId: it.dealItemId,
          retailItemNumber: di?.retailItemNumber ?? null,
          description: di?.description ?? null,
        };
      })
      .sort((a, b) =>
        (a.retailItemNumber ?? 0) - (b.retailItemNumber ?? 0)
      );
  }, [itemsQ.data, dealItemMap, selectedGroup]);

  const availableItems = useMemo(() => {
    if (!selectedGroup) return [];
    const assignedItemIds = new Set(
      assignedItems.map((i) => i.dealItemId).filter((id): id is number => id != null)
    );
    return (dealItemsQ.data ?? [])
      .filter((di) => !assignedItemIds.has(di.dealItemId))
      .map((di) => ({
        dealItemId: di.dealItemId,
        retailItemNumber: di.retailItemNumber,
        description: di.itemDescription,
      }))
      .sort((a, b) => a.retailItemNumber - b.retailItemNumber);
  }, [dealItemsQ.data, assignedItems, selectedGroup]);

  // ---- Handlers -----------------------------------------------------------

  const handleAddGroup = async (typeCode: "M" | "R") => {
    setStatus(null);
    try {
      const created = await addGroup.mutateAsync({
        rewardItemTypeCode: typeCode,
        rewardItemIsFree: false,
        rewardAmount: null,
        rewardPercent: null,
      });
      setSelectedGroup({
        typeCode: created.rewardItemTypeCode,
        groupId: created.rewardItemGroupId,
      });
      setStatus({
        text: `Added ${itemTypeLabel(typeCode)} Group ${created.rewardItemGroupId}.`,
        ok: true,
      });
    } catch (err) {
      setStatus({
        text: `Add group failed: ${err instanceof Error ? err.message : "unknown"}`,
        ok: false,
      });
    }
  };

  const handleRemoveGroup = async () => {
    if (!selectedGroupRow) return;
    const label = groupListLabel(selectedGroupRow);
    if (!confirm(`Remove ${label}? Any assigned items/families must be cleared first.`))
      return;
    setStatus(null);
    try {
      await delGroup.mutateAsync({
        itemType: selectedGroupRow.rewardItemTypeCode,
        rewardItemGroupId: selectedGroupRow.rewardItemGroupId,
      });
      setStatus({ text: `Removed ${label}.`, ok: true });
    } catch (err) {
      setStatus({
        text: `Remove failed: ${err instanceof Error ? err.message : "unknown"}. Clear assignments first.`,
        ok: false,
      });
    }
  };

  // Add selected (right → left). Family mode: POST DealRewardFamily.
  // UPC mode: POST DealRewardItem with addedByFamily=false.
  const handleAddSelected = async () => {
    if (!selectedGroup || availableSelected.length === 0) return;
    setStatus(null);
    let added = 0;
    let failed = 0;
    const parsedScanQty = scanQty.trim() === "" ? null : Number(scanQty);
    const scanQtyToSend = Number.isFinite(parsedScanQty as number)
      ? (parsedScanQty as number)
      : null;
    for (const key of availableSelected) {
      try {
        if (addMode === "Family") {
          await addFamily.mutateAsync({
            rewardItemTypeCode: selectedGroup.typeCode,
            rewardItemGroupId: selectedGroup.groupId,
            familyCode: Number(key),
          });
        } else {
          await addItem.mutateAsync({
            rewardItemTypeCode: selectedGroup.typeCode,
            rewardItemGroupId: selectedGroup.groupId,
            dealItemId: Number(key),
            addedByFamily: false,
            scanQuantity: scanQtyToSend,
          });
        }
        added += 1;
      } catch {
        failed += 1;
      }
    }
    setAvailableSelected([]);
    setStatus({
      text:
        failed === 0
          ? `Added ${added} ${addMode === "Family" ? "family" : "item"}(s).`
          : `Added ${added}, failed ${failed}.`,
      ok: failed === 0,
    });
  };

  const handleAddAll = async () => {
    const allKeys =
      addMode === "Family"
        ? availableFamilies
            .map((av) => (av.rowId == null ? null : String(av.rowId)))
            .filter((v): v is string => v != null)
        : availableItems.map((di) => String(di.dealItemId));
    setAvailableSelected(allKeys);
    // Defer to next event-loop tick so the state update lands before
    // handleAddSelected reads it.
    setTimeout(() => {
      void (async () => {
        if (!selectedGroup) return;
        setStatus(null);
        let added = 0;
        let failed = 0;
        const parsedScanQty = scanQty.trim() === "" ? null : Number(scanQty);
        const scanQtyToSend = Number.isFinite(parsedScanQty as number)
          ? (parsedScanQty as number)
          : null;
        for (const key of allKeys) {
          try {
            if (addMode === "Family") {
              await addFamily.mutateAsync({
                rewardItemTypeCode: selectedGroup.typeCode,
                rewardItemGroupId: selectedGroup.groupId,
                familyCode: Number(key),
              });
            } else {
              await addItem.mutateAsync({
                rewardItemTypeCode: selectedGroup.typeCode,
                rewardItemGroupId: selectedGroup.groupId,
                dealItemId: Number(key),
                addedByFamily: false,
                scanQuantity: scanQtyToSend,
              });
            }
            added += 1;
          } catch {
            failed += 1;
          }
        }
        setAvailableSelected([]);
        setStatus({
          text:
            failed === 0
              ? `Added ${added} ${addMode === "Family" ? "family" : "item"}(s).`
              : `Added ${added}, failed ${failed}.`,
          ok: failed === 0,
        });
      })();
    }, 0);
  };

  const handleRemoveSelected = async () => {
    if (!selectedGroup || assignedSelected.length === 0) return;
    setStatus(null);
    let removed = 0;
    let failed = 0;
    for (const key of assignedSelected) {
      try {
        if (addMode === "Family") {
          await delFamily.mutateAsync({
            itemType: selectedGroup.typeCode,
            familyCode: Number(key),
          });
        } else {
          // UPC mode: `key` is the rewardItemId (the surrogate PK of the
          // DEAL_REWARD_PROMO_ITEM row, not the dealItemId).
          await delItem.mutateAsync(Number(key));
        }
        removed += 1;
      } catch {
        failed += 1;
      }
    }
    setAssignedSelected([]);
    setStatus({
      text:
        failed === 0
          ? `Removed ${removed}.`
          : `Removed ${removed}, failed ${failed}.`,
      ok: failed === 0,
    });
  };

  const handleRemoveAll = async () => {
    const allKeys =
      addMode === "Family"
        ? assignedFamilies.map((f) => String(f.familyCode))
        : assignedItems.map((i) => String(i.rewardItemId));
    if (allKeys.length === 0) return;
    if (!confirm(`Remove all ${allKeys.length} ${addMode === "Family" ? "families" : "items"} from this group?`))
      return;
    setAssignedSelected(allKeys);
    setTimeout(() => {
      void (async () => {
        if (!selectedGroup) return;
        setStatus(null);
        let removed = 0;
        let failed = 0;
        for (const key of allKeys) {
          try {
            if (addMode === "Family") {
              await delFamily.mutateAsync({
                itemType: selectedGroup.typeCode,
                familyCode: Number(key),
              });
            } else {
              await delItem.mutateAsync(Number(key));
            }
            removed += 1;
          } catch {
            failed += 1;
          }
        }
        setAssignedSelected([]);
        setStatus({
          text:
            failed === 0
              ? `Removed ${removed}.`
              : `Removed ${removed}, failed ${failed}.`,
          ok: failed === 0,
        });
      })();
    }, 0);
  };

  // ---- Early returns ------------------------------------------------------

  if (!rewardQ.isLoading && !rewardQ.data) {
    return (
      <div className={s.wrap}>
        <Link to={`/dm/deals/${dealId}/reward`} className={s.backLink}>
          ← Back to reward
        </Link>
        <h2 className={s.title} style={{ margin: "1rem 0" }}>
          No reward configured
        </h2>
        <p style={{ color: "#475569" }}>
          This deal does not have a reward configured yet. Create one from
          the reward editor before adding reward items.
        </p>
        <Link to={`/dm/deals/${dealId}/reward`}>
          <Button themeColor="primary">Go to reward editor</Button>
        </Link>
      </div>
    );
  }

  // ---- Render -------------------------------------------------------------

  return (
    <div className={s.wrap}>
      <Link to={`/dm/deals/${dealId}/reward`} className={s.backLink}>
        ← Back to reward
      </Link>

      <div className={s.titleRow}>
        <h2 className={s.title}>Reward Items</h2>
        <div className={s.titleActions}>
          <Link to={`/dm/deals/${dealId}/reward`}>
            <Button>Reward Header</Button>
          </Link>
          {rewardQ.data?.autoBilling && (
            <Link to={`/dm/deals/${dealId}/reward/billing`}>
              <Button>Reward Billing</Button>
            </Link>
          )}
        </div>
      </div>

      {/* ============================================================ */}
      {/* Card 1 — Reward Header Information (read-only)               */}
      {/* ============================================================ */}
      <section className={s.card}>
        <h3 className={s.sectionTitle}>Reward Header Information</h3>
        <div className={s.formGrid}>
          <FormRow label="Reward Description :">
            <span className={s.fieldValue}>
              {reward?.description ?? "—"}
            </span>
          </FormRow>
          <FormRow label="Promotion Code/Description :">
            <span className={s.fieldValue}>{promoCodeDisplay}</span>
          </FormRow>
          <FormRow label="Buy :">
            <span className={s.fieldValue}>{buyDisplay}</span>
          </FormRow>
          <FormRow label={`${forGetMode} :`}>
            <span className={s.fieldValue}>{forGetDisplay}</span>
          </FormRow>
        </div>
      </section>

      {/* ============================================================ */}
      {/* Card 2 — Reward Item Group Information                       */}
      {/* ============================================================ */}
      <section className={s.card}>
        <h3 className={s.sectionTitle}>Reward Item Group Information</h3>
        <div className={s.groupSplit}>
          {/* Left column: listbox + action links */}
          <div className={s.groupListCol}>
            {sortedGroups.length === 0 ? (
              <div className={s.groupListEmpty}>No item groups defined</div>
            ) : (
              <select
                className={s.groupList}
                size={Math.max(sortedGroups.length, 6)}
                value={
                  selectedGroup
                    ? composeKey(selectedGroup.typeCode, selectedGroup.groupId)
                    : ""
                }
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  const [typeCode, groupId] = v.split(":");
                  setSelectedGroup({
                    typeCode,
                    groupId: Number(groupId),
                  });
                }}
              >
                {sortedGroups.map((g) => {
                  const key = composeKey(g.rewardItemTypeCode, g.rewardItemGroupId);
                  return (
                    <option key={key} value={key}>
                      {groupListLabel(g)}
                    </option>
                  );
                })}
              </select>
            )}

            <div className={s.groupActions}>
              <Button
                size="small"
                onClick={() => handleAddGroup("M")}
                disabled={addGroup.isPending}
              >
                Add Mandatory Group
              </Button>
              <Button
                size="small"
                onClick={() => handleAddGroup("R")}
                disabled={addGroup.isPending}
              >
                Add Reward Group
              </Button>
              <Button
                size="small"
                themeColor="error"
                fillMode="outline"
                onClick={handleRemoveGroup}
                disabled={delGroup.isPending || !selectedGroupRow}
              >
                Remove Group
              </Button>
            </div>
          </div>

          {/* Right column: Selected Group Detail */}
          <div>
            <h4 className={s.subSectionTitle}>Selected Group Detail</h4>
            {!selectedGroupRow ? (
              <p className={s.empty}>
                Select a group on the left, or add a new one.
              </p>
            ) : (
              <div className={s.formGrid}>
                <FormRow label="Group Type :">
                  <span className={s.fieldValue}>
                    {itemTypeLabel(selectedGroupRow.rewardItemTypeCode)}
                  </span>
                </FormRow>
                <FormRow label="Group Number :">
                  <span className={s.fieldValue}>
                    {selectedGroupRow.rewardItemGroupId}
                  </span>
                </FormRow>
                <FormRow label="Reward Amount :">
                  <span className={s.fieldValue}>
                    {selectedGroupRow.rewardAmount != null
                      ? `$${selectedGroupRow.rewardAmount.toFixed(2)}`
                      : "—"}
                  </span>
                </FormRow>
                <FormRow label="Reward Percent :">
                  <span className={s.fieldValue}>
                    {selectedGroupRow.rewardPercent != null
                      ? `${selectedGroupRow.rewardPercent}%`
                      : "—"}
                  </span>
                </FormRow>
                <FormRow label="Free Item ?">
                  <span className={s.fieldValue}>
                    {selectedGroupRow.rewardItemIsFree ? "Yes" : "No"}
                  </span>
                </FormRow>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* Card 3 — Items for Selected Group                            */}
      {/* ============================================================ */}
      {selectedGroupRow && (
        <section className={s.card}>
          <div className={s.card3Header}>
            <h3 className={s.sectionTitle} style={{ margin: 0, borderBottom: "none", paddingBottom: 0 }}>
              Items for Selected Group ({groupListLabel(selectedGroupRow)})
            </h3>
            <div className={s.modeRow}>
              <span className={s.modeLabel}>Add/Remove by :</span>
              <RadioButton
                name="rwrd-mode"
                value="Family"
                label="Family"
                checked={addMode === "Family"}
                onChange={() => setAddMode("Family")}
              />
              <RadioButton
                name="rwrd-mode"
                value="Upc"
                label="UPC"
                checked={addMode === "Upc"}
                onChange={() => setAddMode("Upc")}
              />
              <Link
                to={`/dm/deals/${dealId}/items/add?addToReward=true`}
                className={s.quickAdd}
                title="Open the item-search form to add a new item to the deal and attach it to this reward"
              >
                Quick add via item search…
              </Link>
            </div>
          </div>

          <div className={s.transferLayout}>
            {/* LEFT listbox: ASSIGNED to selected group */}
            <div className={s.transferListWrap}>
              <label className={s.scanQtyLabel}>
                Assigned ({addMode === "Family" ? assignedFamilies.length : assignedItems.length})
              </label>
              <select
                className={s.transferList}
                multiple
                value={assignedSelected}
                onChange={(e) =>
                  setAssignedSelected(
                    Array.from(e.target.selectedOptions).map((o) => o.value)
                  )
                }
              >
                {addMode === "Family"
                  ? assignedFamilies.map((f) => (
                      <option key={f.familyCode} value={String(f.familyCode)}>
                        Family: {f.familyCode}
                        {f.description ? ` ${f.description}` : ""}
                      </option>
                    ))
                  : assignedItems.map((i) => (
                      <option key={i.rewardItemId} value={String(i.rewardItemId)}>
                        Upc: {i.retailItemNumber ?? i.dealItemId}
                        {i.description ? ` ${i.description}` : ""}
                      </option>
                    ))}
              </select>
            </div>

            {/* CENTER: scan qty + 4 arrow buttons */}
            <div className={s.transferControls}>
              <div className={s.scanQtyBlock}>
                <label className={s.scanQtyLabel}>Scan Quantity</label>
                <Input
                  value={scanQty}
                  onChange={(e) => setScanQty(String(e.value ?? ""))}
                  style={{ width: "70px", textAlign: "center" }}
                />
              </div>
              <Button
                className={s.arrowBtn}
                onClick={handleRemoveSelected}
                disabled={assignedSelected.length === 0}
                title="Remove selected"
              >
                {"<"}
              </Button>
              <Button
                className={s.arrowBtn}
                onClick={handleRemoveAll}
                disabled={
                  (addMode === "Family"
                    ? assignedFamilies.length
                    : assignedItems.length) === 0
                }
                title="Remove all"
              >
                {"<<"}
              </Button>
              <Button
                className={s.arrowBtn}
                onClick={handleAddSelected}
                disabled={availableSelected.length === 0}
                title="Add selected"
              >
                {">"}
              </Button>
              <Button
                className={s.arrowBtn}
                onClick={handleAddAll}
                disabled={
                  (addMode === "Family"
                    ? availableFamilies.length
                    : availableItems.length) === 0
                }
                title="Add all"
              >
                {">>"}
              </Button>
            </div>

            {/* RIGHT listbox: AVAILABLE to add */}
            <div className={s.transferListWrap}>
              <label className={s.scanQtyLabel}>
                Available ({addMode === "Family" ? availableFamilies.length : availableItems.length})
              </label>
              <select
                className={s.transferList}
                multiple
                value={availableSelected}
                onChange={(e) =>
                  setAvailableSelected(
                    Array.from(e.target.selectedOptions).map((o) => o.value)
                  )
                }
              >
                {addMode === "Family"
                  ? availableFamilies.map((av) => (
                      <option
                        key={String(av.rowId)}
                        value={String(av.rowId ?? "")}
                      >
                        Family: {av.rowId}
                        {av.description ? ` ${av.description}` : ""}
                      </option>
                    ))
                  : availableItems.map((di) => (
                      <option key={di.dealItemId} value={String(di.dealItemId)}>
                        Upc: {di.retailItemNumber}
                        {di.description ? ` ${di.description}` : ""}
                      </option>
                    ))}
              </select>
            </div>
          </div>

          {status && (
            <p
              className={`${s.status} ${status.ok ? s.statusOk : s.statusErr}`}
              role="status"
            >
              {status.text}
            </p>
          )}
        </section>
      )}

      {/* ============================================================ */}
      {/* Bulk actions — promote all reward items to mandatory          */}
      {/* ============================================================ */}
      <section className={s.card}>
        <h3 className={s.sectionTitle}>Bulk actions</h3>
        <p className={s.empty} style={{ margin: "0 0 0.75rem", padding: 0 }}>
          Set every reward item on this reward to the mandatory item type
          (REWARD_ITEM_TYPE_CD = M). Mirrors the legacy "Promote items"
          action surfaced on the reward editor.
        </p>
        <div className={s.actions}>
          <Button
            themeColor="primary"
            disabled={promote.isPending || (itemsQ.data?.length ?? 0) === 0}
            onClick={() => {
              if (
                confirm(
                  "Promote every item on this reward to mandatory (ITM_TYP_CD=M)?"
                )
              )
                promote.mutate(undefined, {
                  onSuccess: (r) =>
                    alert(`Promoted ${r.promoted} reward item(s).`),
                  onError: (e) =>
                    alert(
                      `Promote failed: ${e instanceof Error ? e.message : "unknown"}`
                    ),
                });
            }}
          >
            {promote.isPending ? "Promoting…" : "Promote all items to Mandatory"}
          </Button>
        </div>
      </section>
    </div>
  );
};

export default EditRewardItems;
